import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle, Info, Loader2 } from "lucide-react";
import { Plan, Platform } from "@/types/enums";
import {
  AdminRefundMode,
  type AdminSubscriptionItem,
} from "@/types/subscription";
import {
  useRefundSubscription,
  useRefundablePayment,
} from "@/hooks/use-subscriptions";
import { PlanBadge } from "./plan-badge";
import { StatusBadge } from "./status-badge";

const DEFAULT_REFUND_REASON = "관리자 환불 처리";

// 서버 PLAN_REWARD_CREDIT[FREE] 와 동일해야 한다.
const FREE_PLAN_MONTHLY_CREDITS = 500;

const tossRefundSchema = z.object({
  amount: z
    .number({ error: "환불 금액을 입력해주세요" })
    .int({ error: "정수만 입력 가능합니다" })
    .positive({ error: "1원 이상 입력해주세요" }),
  reason: z.string().min(1, { error: "환불 사유를 입력해주세요" }),
});

const freeOnlySchema = z.object({
  reason: z.string().min(1, { error: "환불 사유를 입력해주세요" }),
});

type TossRefundFormValues = z.infer<typeof tossRefundSchema>;
type FreeOnlyFormValues = z.infer<typeof freeOnlySchema>;

interface RefundDialogProps {
  subscription: AdminSubscriptionItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RefundDialog({
  subscription,
  open,
  onOpenChange,
}: RefundDialogProps) {
  const { mutate: refund, isPending } = useRefundSubscription();
  const { data: refundable, isLoading } = useRefundablePayment(
    subscription.userId,
    open,
  );

  const isFreePlan = subscription.plan === Plan.FREE;
  const refundMode = refundable?.refundMode;
  const canAmountRefund =
    !!refundable?.refundable && refundable?.requiresAmount === true;
  const canProratedRefund =
    !!refundable?.refundable && refundMode === AdminRefundMode.ANDROID_REFUND;
  const canFreeOnly =
    !refundable?.refundable && !isLoading && !!refundable?.freeOnlyAvailable;
  const isIos = subscription.platform === Platform.IOS;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>구독 환불 처리</DialogTitle>
          <DialogDescription>
            플랫폼에 따라 부분 환불 또는 FREE 전환을 진행합니다.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              결제 정보 조회 중...
            </span>
          </div>
        ) : isFreePlan ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>환불 불가</AlertTitle>
            <AlertDescription>
              FREE 플랜은 환불할 수 없습니다.
            </AlertDescription>
          </Alert>
        ) : canAmountRefund && refundMode ? (
          <AmountRefundForm
            subscription={subscription}
            refundable={refundable!}
            refundMode={refundMode}
            isPending={isPending}
            onSubmit={(values) =>
              refund(
                {
                  userId: subscription.userId,
                  mode: refundMode,
                  amount: values.amount,
                  reason: values.reason,
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            onCancel={() => onOpenChange(false)}
          />
        ) : canProratedRefund ? (
          <ProratedRefundForm
            subscription={subscription}
            isPending={isPending}
            onSubmit={(values) =>
              refund(
                {
                  userId: subscription.userId,
                  mode: AdminRefundMode.ANDROID_REFUND,
                  reason: values.reason,
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            onFreeOnly={(values) =>
              refund(
                {
                  userId: subscription.userId,
                  mode: AdminRefundMode.FREE_ONLY,
                  reason: values.reason,
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            onCancel={() => onOpenChange(false)}
          />
        ) : canFreeOnly ? (
          <FreeOnlyForm
            subscription={subscription}
            reason={refundable?.reason}
            isIos={isIos}
            isPending={isPending}
            onSubmit={(values) =>
              refund(
                {
                  userId: subscription.userId,
                  mode: AdminRefundMode.FREE_ONLY,
                  reason: values.reason,
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>처리 불가</AlertTitle>
            <AlertDescription>
              {refundable?.reason ?? "환불 가능 정보를 불러올 수 없습니다."}
            </AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface AmountRefundFormProps {
  subscription: AdminSubscriptionItem;
  refundable: NonNullable<
    ReturnType<typeof useRefundablePayment>["data"]
  >;
  refundMode: AdminRefundMode;
  isPending: boolean;
  onSubmit: (values: TossRefundFormValues) => void;
  onCancel: () => void;
}

function AmountRefundForm({
  subscription,
  refundable,
  refundMode,
  isPending,
  onSubmit,
  onCancel,
}: AmountRefundFormProps) {
  const balanceAmount = refundable.balanceAmount ?? 0;
  const totalAmount = refundable.totalAmount ?? 0;
  const isPartiallyCanceled = refundable.status === "PARTIAL_CANCELED";
  const isStripe = refundMode === AdminRefundMode.STRIPE_REFUND;
  const providerLabel = isStripe ? "Stripe" : "토스";
  const currency = refundable.currency ?? "KRW";
  const formatAmount = (n: number) =>
    currency === "KRW"
      ? `${n.toLocaleString()}원`
      : `${n.toLocaleString()} ${currency}`;

  const form = useForm<TossRefundFormValues>({
    resolver: zodResolver(tossRefundSchema),
    defaultValues: {
      amount: balanceAmount,
      reason: DEFAULT_REFUND_REASON,
    },
  });

  useEffect(() => {
    form.reset({ amount: balanceAmount, reason: DEFAULT_REFUND_REASON });
  }, [balanceAmount, form]);

  const handleSubmit = (values: TossRefundFormValues) => {
    if (values.amount > balanceAmount) {
      form.setError("amount", {
        type: "max",
        message: `환불 가능 잔액(${formatAmount(balanceAmount)})을 초과할 수 없습니다`,
      });
      return;
    }
    onSubmit(values);
  };

  return (
    <>
      <div className="space-y-4 py-2">
        <div className="rounded-lg border p-4 space-y-3">
          <SubscriptionInfo subscription={subscription} />
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">마지막 결제일</span>
            <span className="text-sm">
              {refundable.approvedAt
                ? new Date(refundable.approvedAt).toLocaleString("ko-KR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">결제 수단</span>
            <span className="text-sm">
              {providerLabel}
              {refundable.method ? ` (${refundable.method})` : ""}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">결제 금액</span>
            <span className="font-mono text-sm">{formatAmount(totalAmount)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">환불 가능 잔액</span>
            <span className="font-mono text-sm font-semibold">
              {formatAmount(balanceAmount)}
              {isPartiallyCanceled && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (부분 환불됨)
                </span>
              )}
            </span>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>환불 후 변경사항:</strong>
            <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
              <li>입력한 금액이 {providerLabel}를 통해 환불됩니다</li>
              {isStripe && <li>Stripe 구독이 즉시 취소됩니다</li>}
              <li>
                부분 환불이어도 사용자 플랜은 FREE로 전환되고 월간 크레딧이{" "}
                {FREE_PLAN_MONTHLY_CREDITS}으로 초기화됩니다
              </li>
              <li>
                구매 크레딧 (
                {subscription.userCredits.purchased.toLocaleString()})은 유지됩니다
              </li>
              <li>이 작업은 되돌릴 수 없습니다</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>환불 금액 ({currency})</FormLabel>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => field.onChange(balanceAmount)}
                    >
                      전액 ({formatAmount(balanceAmount)})
                    </Button>
                  </div>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={balanceAmount}
                      step={1}
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? 0 : Number(e.target.value),
                        )
                      }
                      aria-label="Refund amount"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>환불 사유</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="환불 사유를 상세히 입력해주세요..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          취소
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={isPending}
          aria-label="Confirm refund"
        >
          {isPending ? "처리 중..." : "환불 확인"}
        </Button>
      </DialogFooter>
    </>
  );
}

interface FreeOnlyFormProps {
  subscription: AdminSubscriptionItem;
  reason?: string;
  isIos?: boolean;
  isPending: boolean;
  onSubmit: (values: FreeOnlyFormValues) => void;
  onCancel: () => void;
}

function FreeOnlyForm({
  subscription,
  reason: blockReason,
  isIos,
  isPending,
  onSubmit,
  onCancel,
}: FreeOnlyFormProps) {
  const form = useForm<FreeOnlyFormValues>({
    resolver: zodResolver(freeOnlySchema),
    defaultValues: { reason: DEFAULT_REFUND_REASON },
  });

  const platformHint =
    subscription.platform && subscription.platform !== Platform.TOSS
      ? `${subscription.platform}은 해당 스토어/대시보드에서 결제 환불을 별도로 처리하세요.`
      : blockReason ?? "토스 환불은 불가능하지만 구독을 FREE로 전환할 수 있습니다.";

  return (
    <>
      <div className="space-y-4 py-2">
        {isIos && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>iOS는 어드민에서 환불할 수 없습니다</AlertTitle>
            <AlertDescription>
              Apple App Store 결제는 개발자가 직접 환불할 수 없습니다. 사용자가
              직접 App Store(reportaproblem.apple.com)에서 환불을 요청해야
              합니다. 여기서는 접근 권한만 FREE로 전환할 수 있습니다.
            </AlertDescription>
          </Alert>
        )}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>FREE 전환만 진행</AlertTitle>
          <AlertDescription>
            {platformHint}
            <br />
            이 다이얼로그에서는 사용자 구독을 FREE로 떨어뜨리기만 합니다.
            결제 환불은 발생하지 않습니다.
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border p-4 space-y-3">
          <SubscriptionInfo subscription={subscription} />
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>실행 시 변경사항:</strong>
            <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
              <li>플랜이 FREE로 전환됩니다</li>
              <li>월간 크레딧이 {FREE_PLAN_MONTHLY_CREDITS}으로 초기화됩니다</li>
              <li>
                구매 크레딧 (
                {subscription.userCredits.purchased.toLocaleString()})은 유지됩니다
              </li>
              <li>토스/스토어로의 환불 요청은 발생하지 않습니다</li>
              <li>이 작업은 되돌릴 수 없습니다</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>전환 사유</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="예: App Store 환불 처리됨, Stripe 대시보드에서 환불 완료 등"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          취소
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={form.handleSubmit(onSubmit)}
          disabled={isPending}
          aria-label="Confirm free-only conversion"
        >
          {isPending ? "처리 중..." : "FREE로 전환"}
        </Button>
      </DialogFooter>
    </>
  );
}

interface ProratedRefundFormProps {
  subscription: AdminSubscriptionItem;
  isPending: boolean;
  onSubmit: (values: FreeOnlyFormValues) => void;
  onFreeOnly: (values: FreeOnlyFormValues) => void;
  onCancel: () => void;
}

function ProratedRefundForm({
  subscription,
  isPending,
  onSubmit,
  onFreeOnly,
  onCancel,
}: ProratedRefundFormProps) {
  const form = useForm<FreeOnlyFormValues>({
    resolver: zodResolver(freeOnlySchema),
    defaultValues: { reason: DEFAULT_REFUND_REASON },
  });

  return (
    <>
      <div className="space-y-4 py-2">
        <div className="rounded-lg border p-4 space-y-3">
          <SubscriptionInfo subscription={subscription} />
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>환불 후 변경사항:</strong>
            <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
              <li>
                Google Play가 남은 기간 기준 일할(prorated) 금액을 자동
                환불합니다 (금액은 Google이 계산)
              </li>
              <li>구독이 즉시 revoke(해지)됩니다</li>
              <li>
                사용자 플랜은 FREE로 전환되고 월간 크레딧이{" "}
                {FREE_PLAN_MONTHLY_CREDITS}으로 초기화됩니다
              </li>
              <li>
                구매 크레딧 (
                {subscription.userCredits.purchased.toLocaleString()})은 유지됩니다
              </li>
              <li>이 작업은 되돌릴 수 없습니다</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>환불 사유</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="환불 사유를 상세히 입력해주세요..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <div className="rounded-lg border border-dashed p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Info className="h-4 w-4" />
            이미 Google Play 콘솔에서 환불하셨나요?
          </div>
          <p className="text-sm text-muted-foreground">
            콘솔에서 이미 환불을 완료했다면 Google 재환불 없이 구독만 FREE로
            전환하세요. 위쪽 일할 환불 + 해지 버튼은 Google에 다시 환불을
            요청합니다.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={form.handleSubmit(onFreeOnly)}
            disabled={isPending}
            aria-label="Convert to free only"
          >
            FREE 전환만 하기
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          취소
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={form.handleSubmit(onSubmit)}
          disabled={isPending}
          aria-label="Confirm prorated refund"
        >
          {isPending ? "처리 중..." : "일할 환불 + 해지"}
        </Button>
      </DialogFooter>
    </>
  );
}

function SubscriptionInfo({
  subscription,
}: {
  subscription: AdminSubscriptionItem;
}) {
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">사용자</span>
        <span className="text-sm">{subscription.userName}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">이메일</span>
        <span className="text-sm">{subscription.userEmail}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">현재 플랜</span>
        <PlanBadge plan={subscription.plan} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">상태</span>
        <StatusBadge status={subscription.status} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">플랫폼</span>
        <span className="text-sm">{subscription.platform ?? "—"}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">월간 크레딧</span>
        <span className="font-mono text-sm">
          {subscription.userCredits.monthly.toLocaleString()}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">구매 크레딧</span>
        <span className="font-mono text-sm">
          {subscription.userCredits.purchased.toLocaleString()}
        </span>
      </div>
    </>
  );
}
